import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/session';
import { inr, short, date } from '../lib/fmt';
import { Card, Note, PageHead, Tile, Tiles, Table, Th, Td, TableWrap, Empty, Tag } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton, BarsSkeleton } from '../components/skeleton';

interface Row {
  _id: string; txn_no: string; txn_date: string; direction: 'in' | 'out';
  amount: number; running: number; narration?: string | null; party_text?: string | null;
  category?: { name: string } | null; counter_account?: { name: string } | null;
}
interface Feed {
  account: { name: string; holder?: string; purpose?: string; opening_date?: string };
  opening: number; closing: number; rows: Row[];
}

/** Head, the balances, then the ledger itself. */
function StatementSkeleton() {
  return (
    <Loading label="Loading the account statement">
      <HeadSkeleton />
      <TilesSkeleton />
      <CardSkeleton flush head={false}><TableSkeleton rows={9} cols={6} /></CardSkeleton>
    </Loading>
  );
}

export default function AccountStatement() {
  const { id } = useParams();
  const { data, loading, error } = useAsync<Feed>(
    () => api.get(`/api/accounts/${id}/statement`, { limit: 400 }), [id]);

  if (loading) return <StatementSkeleton />;
  if (error) return <Note title="That did not load" tone="critical">{error}</Note>;
  const d = data!;

  const moneyIn = d.rows.filter((r) => r.direction === 'in').reduce((s, r) => s + r.amount, 0);
  const moneyOut = d.rows.filter((r) => r.direction === 'out').reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <PageHead
        eyebrow={<Link to="/accounts" className="text-inherit no-underline">← Accounts</Link>}
        title={d.account.name}
        dek={[d.account.holder && `Held by ${d.account.holder}.`, d.account.purpose].filter(Boolean).join(' ')} />

      <Tiles>
        <Tile hero label="Closing balance" value={inr(d.closing)}
              sub={<span className="text-muted">{d.rows.length} entries</span>} />
        <Tile label="Opening" value={inr(d.opening)}
              sub={<span className="text-muted">
                {d.account.opening_date ? `as at ${date(d.account.opening_date)}` : 'start of records'}</span>} />
        <Tile label="Money in" value={short(moneyIn)} />
        <Tile label="Money out" value={short(moneyOut)} />
      </Tiles>

      <Card flush>
        {!d.rows.length ? <Empty>Nothing has moved through this account yet.</Empty> : (
          <TableWrap scroll>
            <Table cards>
              <thead><tr>
                <Th>Date</Th><Th>Ref</Th><Th>Details</Th><Th>Category</Th>
                <Th n>In</Th><Th n>Out</Th><Th n>Balance</Th>
              </tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r._id}>
                    <Td label="Date" className="font-mono text-[11.5px]">{date(r.txn_date)}</Td>
                    <Td label="Ref" className="font-mono text-[11.5px]">{r.txn_no}</Td>
                    <Td label="Details">
                      {r.narration ?? r.party_text ?? '—'}
                      {r.counter_account && <> <Tag>{r.counter_account.name}</Tag></>}
                    </Td>
                    <Td label="Category" className="text-ink-2">{r.category?.name ?? '—'}</Td>
                    <Td label="In" n className="text-good">{r.direction === 'in' ? inr(r.amount) : ''}</Td>
                    <Td label="Out" n>{r.direction === 'out' ? inr(r.amount) : ''}</Td>
                    <Td label="Balance" n><b>{inr(r.running)}</b></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
